import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { FeatureCollection } from "@turf/turf";
import HorizontalScrollContainer from "common/components/HorizontalScrollContainer";
import { AnimalsIcon } from "common/icons";
import { useTranslation } from "common/locales";
import type { Feature } from "geojson";
import {
  getAllFieldsForAnimalGroupIdFromArray,
  getAnimalGroupColorFromArray,
  getAvailableDryMatterForAnimalGroupIdFromArray,
  useCurrentFarmAnimalGroups,
  useCurrentFarmStockClasses,
  useFields,
} from "model";
import { useActiveFeedBudgetDemand } from "model/useActiveFeedBudgetDemand";
import { useAnimalGroup } from "model/useAnimalGroupQueries";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AnalyticsEvents, analytics } from "../../common/analytics";
import FieldMapPreview from "./FieldMapPreview";

const AnimalCard = styled(Card)({
  minWidth: 280,
  maxWidth: 320,
  cursor: "pointer",
  transition: "transform 0.2s",
  backgroundColor: "white",
  borderRadius: 16,
  "&:hover": {
    transform: "translateY(-2px)",
  },
});

const MetricsContainer = styled("div")({
  padding: "8px 0px",
});

const HeaderRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 8,
});

const IconCircle = styled("div")<{ color: string }>(({ color }) => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
  backgroundColor: color,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const MapPreviewContainer = styled("div")({
  height: 120,
  background: "#f5f5f5",
  borderRadius: "16px 16px 0 0",
  position: "relative",
  overflow: "hidden",
  margin: "-16px -16px 16px -16px",
});

const MapPlaceholderText = styled(Typography)({
  fontWeight: 500,
  letterSpacing: "0.5px",
  opacity: 0.8,
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
});

interface MetricItemProps {
  label: string;
  value: string | number;
  unit?: string;
  warning?: boolean;
}

function MetricItem({ label, value, unit, warning }: MetricItemProps) {
  return (
    <Tooltip title={String(value)} enterDelay={500} enterNextDelay={500}>
      <Box
        sx={{
          py: 0.75,
          borderBottom: "1px solid #F0F0F0",
          "&:last-child": {
            borderBottom: "none",
          },
          minWidth: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", fontSize: "0.7rem" }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: warning ? "#DC2626" : "#1a1a2e",
              fontStyle: warning ? "italic" : "normal",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </Typography>
          {unit && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.7rem",
                flexShrink: 0,
              }}
            >
              {unit}
            </Typography>
          )}
        </Box>
      </Box>
    </Tooltip>
  );
}

interface WarningItemProps {
  label: string;
  value: string;
}

function WarningItem({ label, value }: WarningItemProps) {
  return (
    <Box
      sx={{
        py: 0.75,
        px: 1,
        mx: -1,
        borderBottom: "1px solid #F7E880",
        backgroundColor: "#FDFBEB",
        borderRadius: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "#A48137", display: "block", fontSize: "0.7rem" }}
      >
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600, color: "#A48137" }}>
        {value}
      </Typography>
    </Box>
  );
}

interface AnimalGroupCardProps {
  groupId: string;
  onCardClick: () => void;
}

const AnimalGroupCard: React.FC<AnimalGroupCardProps> = ({
  groupId,
  onCardClick,
}) => {
  const { t } = useTranslation();

  // Use React Query hooks
  const { data: animalGroup } = useAnimalGroup(groupId);
  const { data: allAnimalGroups } = useCurrentFarmAnimalGroups();
  const { data: allStockClasses } = useCurrentFarmStockClasses();

  // Filter stock classes for this animal group
  const stockClasses = useMemo(
    () => allStockClasses.filter((sc) => sc.animalGroup === groupId),
    [allStockClasses, groupId]
  );

  // Get color from array helper
  const groupColor = useMemo(
    () => getAnimalGroupColorFromArray(allAnimalGroups, groupId),
    [allAnimalGroups, groupId]
  );

  // Use React Query for fields data
  const { data: allFields = [] } = useFields();

  // Compute derived data from React Query
  const fields = useMemo(
    () => getAllFieldsForAnimalGroupIdFromArray(allFields, groupId),
    [allFields, groupId]
  );
  const dryMatterAvailable = useMemo(
    () => getAvailableDryMatterForAnimalGroupIdFromArray(fields, animalGroup),
    [fields, animalGroup]
  );
  const currentField = useMemo(
    () => fields.find((f) => f.animalGroupNow === animalGroup?.guid),
    [fields, animalGroup?.guid]
  );

  const activeBudgetDemand = useActiveFeedBudgetDemand();

  if (!animalGroup) return null;

  // Use active feed budget plan demand if available, fall back to backend stock classes
  const totalDailyDemand =
    activeBudgetDemand.get(groupId) ??
    (stockClasses
      ? stockClasses.reduce(
          (sum, sc) => sum + sc.consumptionKgPerDay * sc.animalCount,
          0
        )
      : 0);

  // Calculate days of forage available (grazing days ahead)
  const daysOfForage =
    totalDailyDemand > 0
      ? Math.round(dryMatterAvailable / totalDailyDemand)
      : 0;

  // Create field geojson for map preview
  const fieldGeojson: FeatureCollection = {
    type: "FeatureCollection",
    features: fields.map((field) => field.geom),
  };

  return (
    <AnimalCard onClick={onCardClick}>
      <CardContent>
        <MapPreviewContainer>
          {fields.length > 0 ? (
            <FieldMapPreview
              geojson={fieldGeojson}
              isArable={false}
              fieldIds={fields.map((field) => field.guid)}
            />
          ) : (
            <MapPlaceholderText variant="body2">No Fields</MapPlaceholderText>
          )}
        </MapPreviewContainer>

        <HeaderRow>
          <IconCircle color={animalGroup.color || groupColor}>
            <AnimalsIcon fontSize="small" />
          </IconCircle>
          <Typography variant="body1" fontWeight="medium">
            {animalGroup.name}
          </Typography>
        </HeaderRow>

        <MetricsContainer>
          {currentField ? (
            <MetricItem
              label={t("animalGroup.metrics.currentField")}
              value={currentField.name}
            />
          ) : (
            <WarningItem
              label={t("animalGroup.metrics.currentField")}
              value={t("animalGroup.metrics.notSet")}
            />
          )}

          <MetricItem
            label={t("animalGroup.metrics.grazingDaysAhead")}
            value={daysOfForage > 0 ? daysOfForage : "-"}
            unit={daysOfForage > 0 ? "days" : undefined}
          />
        </MetricsContainer>
      </CardContent>
    </AnimalCard>
  );
};

function AnimalGroupCardSkeleton() {
  return (
    <AnimalCard sx={{ pointerEvents: "none" }}>
      <CardContent>
        <MapPreviewContainer>
          <Skeleton variant="rectangular" height="100%" width="100%" />
        </MapPreviewContainer>

        <HeaderRow>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="text" width={100} height={24} />
        </HeaderRow>

        <MetricsContainer>
          <Box sx={{ py: 0.75, borderBottom: "1px solid #F0F0F0" }}>
            <Skeleton variant="text" width={80} height={14} />
            <Skeleton variant="text" width={60} height={20} />
          </Box>
          <Box sx={{ py: 0.75 }}>
            <Skeleton variant="text" width={100} height={14} />
            <Skeleton variant="text" width={50} height={20} />
          </Box>
        </MetricsContainer>
      </CardContent>
    </AnimalCard>
  );
}

export function AnimalGroupCardsSkeleton() {
  return (
    <Box>
      <HorizontalScrollContainer>
        {[1, 2, 3].map((i) => (
          <AnimalGroupCardSkeleton key={i} />
        ))}
      </HorizontalScrollContainer>
    </Box>
  );
}

export default function AnimalGroupCards() {
  const navigate = useNavigate();
  const { data: animalGroups, isLoading } = useCurrentFarmAnimalGroups();
  const loading = isLoading;

  const handleCardClick = (groupId: string) => {
    analytics.track(AnalyticsEvents.ANIMAL_GROUP_VIEWED, { groupId });
    navigate(`/animal-group/${groupId}`);
  };

  if (loading && animalGroups.length === 0) {
    return <AnimalGroupCardsSkeleton />;
  }

  return (
    <Box>
      <HorizontalScrollContainer>
        {animalGroups.map((group) => (
          <AnimalGroupCard
            key={group.guid}
            groupId={group.guid}
            onCardClick={() => handleCardClick(group.guid)}
          />
        ))}
      </HorizontalScrollContainer>
    </Box>
  );
}
